import React, { useEffect, useMemo } from 'react';
import { Button } from 'framework/ui/mui-builder';
import { FormFieldsData, InputListLayoutProps, InputListLayoutType } from 'framework/form';
import {
  BaseInputLayout,
  COMMON_INPUT_LAYOUT_DEFAULT_PROPS,
  getBaseInputLayoutProps,
  getNotNullIndex,
} from 'framework/ui/layout';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useShowContext } from 'framework/show';
import { Stages } from 'domain/shared/candidate';
import { usePositionService, PositionType } from 'domain/shared/employee';
import { CalculatePercentileModalButton } from 'domain/shared/performanceMeasurement';
import { useAsyncValue } from 'framework/utils';

const CandidatePercentileButton: React.FC = () => {
  const { data } = useShowContext();
  const { findAll: findAllPositions } = usePositionService();
  const positions = useAsyncValue(findAllPositions);

  const initialPosition = useMemo(() => {
    const vacancyData = data?.lastAppliedVacancyData;
    if (!vacancyData?.specialization || !positions?.length) {
      return null;
    }

    const position = positions.find((p: PositionType) => p.name === vacancyData.specialization);
    if (!position?.id) {
      return null;
    }

    const { technology } = vacancyData;
    return technology ? `${position.id}--${technology}` : String(position.id);
  }, [data?.lastAppliedVacancyData, positions]);

  if (!positions) {
    return null;
  }

  return <CalculatePercentileModalButton initialPosition={initialPosition} />;
};

const CandidateInputListLayout: InputListLayoutType = ({
  fields,
  addMethod,
  removeMethod,
  inputTemplate: InputComponent,
  onChangeCallback,
  addText = '',
  deleteText = '',
  maxFields,
  minFields,
  source,
  className,
  simpleLayout = true,
  inputProps,
  value,
  disabled,
  disabledRemove,
  isAddable = true,
  isDeletable = true,
  ...otherProps
}: InputListLayoutProps) => {
  const { data } = useShowContext();
  const isDecisionStage = data?.lastActiveApplication?.applicationPipelineState?.stage === Stages.Decision;
  const props = {
    ...COMMON_INPUT_LAYOUT_DEFAULT_PROPS,
    ...otherProps,
  };

  useEffect(() => {
    if (fields.length === 0 && addMethod) {
      addMethod();
    }
  }, [fields.length, addMethod]);

  const removeBtn = (index: number, fieldValue: any) => {
    return (
      <Button
        id={`remove-${index}`}
        size="small"
        color="primary"
        variant="text"
        onClick={() => removeMethod(index)}
        disabled={fields.length <= minFields || disabled || (disabledRemove && disabledRemove(fieldValue))}
        sx={{ padding: '4px', borderRadius: '50%', minWidth: 0 }}
      >
        <CloseIcon />
      </Button>
    );
  };

  return (
    <BaseInputLayout {...getBaseInputLayoutProps({ ...props, source, disabled })}>
      <Box>
        {fields.map((fieldValue, index) => (
          <Box pb={4}>
            <Box
              display="flex"
              sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 3 }}
            >
              <Typography variant="h4">Compensation details {index !== 0 ? index + 1 : ''}</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {index === 0 && isDecisionStage && <CandidatePercentileButton />}
                {isDeletable && (index === 0 ? null : removeBtn(index, fieldValue))}
              </Box>
            </Box>
            {/* eslint-disable-next-line react/no-array-index-key */}
            <div key={`input-${index}`}>
              <InputComponent
                {...inputProps}
                label=""
                isLast={fields.length === index + 1}
                source={fieldValue && `${source}.${getNotNullIndex(fields, index)}`}
                value={fieldValue}
                onChangeCallback={(newValue: FormFieldsData) => {
                  onChangeCallback(index, newValue);
                }}
                className={className || ''}
              />
              {simpleLayout && isDeletable}
            </div>
          </Box>
        ))}
      </Box>
    </BaseInputLayout>
  );
};

export default CandidateInputListLayout;
